import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  Modal,
  Typography,
  Button,
  Row,
  Col,
  Space,
  Input,
  Form,
  Tooltip,
  Divider,
  Select,
  Popconfirm
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  StarFilled,
  StarOutlined
} from '@ant-design/icons';

import {
  popName,
  popDescription,
  popParameters
} from '../../features/groups/group-exercises/pops';
import { getEmbeddedUrl } from '../../utils/videoUrl.utils';
import { parseFavorites } from '../../utils/exercises.utils';
import { toCamelCaseObjKeys } from '../../utils/object.utils';

import V from 'voca';
import cptCodes from '../../features/export-hep-to-prompt/CptCodes.json';
import services from '../../services';
import states from '../../states';
import setNotification from '../../utils/setNotification.utils';
import NoResults from '../../components/NoResults';

const ExerciseDetailsModal = ({
  handleExerciseDetailsModal,
  visibleProfile
}) => {
  const [editing, setEditing] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [parametersError, setParametersError] = useState(false);

  const [exercises, setExercises] = useRecoilState(states.exercises);
  const [favorites, setFavorites] = useRecoilState(states.favorites);

  const user = useRecoilValue(states.user);
  const modals = useRecoilValue(states.modal);

  if (!exercises.details || !modals.exerciseDetails) {
    return null;
  }

  const { video, exerciseName, sets, reps, hold, instructions, id, groupId } =
    exercises.details;

  const item = favorites.list.find((fav) => fav.id === id);
  const star = item ? true : !!exercises.details?.star;
  const embeddedUrl = exercises.details ? getEmbeddedUrl(video) : undefined;
  const params = exercises.custom.parameter.find((p) => p.ExerciseId === id);
  const custom = exercises.custom.exercises.find((ex) => ex.Id === id);

  const handleFavorite = (star) => {
    if (star) {
      handleRemoveFavorite();
    } else {
      handleAddFavorite();
    }
  };

  const handleAddFavorite = async () => {
    if (exceedsFavoriteLimit()) {
      setNotification(
        'info',
        'Notice!',
        'You have reached your 100 favorite exercises limit.'
      );
      return;
    }

    try {
      setFavoriteLoading(true);

      let response = await services.exercises.addFavorite(
        user.details.favoriteId,
        [{ Id: id, GroupId: groupId }]
      );

      if (response.status === 200) {
        setFavorites({
          ...favorites,
          list: parseFavorites(response.data.exercises)
        });
        handleExerciseDetailsModal(null);
        setNotification(
          'success',
          'Success!',
          'Exercise has been added to favorites.'
        );
      }
    } catch (err) {
      setNotification(
        'error',
        'Error!',
        'An error occurred while adding exercise to favorites.'
      );
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleRemoveFavorite = async () => {
    try {
      setFavoriteLoading(true);

      let response = await services.exercises.deleteFavorite(
        user.details.favoriteId,
        id
      );

      if (response.status === 200) {
        setFavorites({
          ...favorites,
          list: parseFavorites(response.data.exercises)
        });
        handleExerciseDetailsModal(null);
        setNotification(
          'success',
          'Success!',
          'Exercise has been removed from favorites.'
        );
      }
    } catch (err) {
      setNotification(
        'error',
        'Error!',
        'An error occurred while removing exercise to favorites.'
      );
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleSaveExercise = async (values) => {
    try {
      setLoading(true);

      if (!!custom) {
        const exercise = {
          ...toCamelCaseObjKeys(custom),
          ...values,
          name: values.exerciseName.toLowerCase()
        };

        delete exercise.exerciseName;

        await services.exercises.addCustomExercise(exercise).then((res) => {
          if (res.status === 200) {
            setExercises((prevState) => {
              const callbackFn = (item) => {
                if (item.id !== id) {
                  return item;
                }

                return {
                  ...toCamelCaseObjKeys(res.data)
                };
              };

              return {
                ...prevState,
                unmapped: prevState.unmapped.map(callbackFn),
                list: prevState.list.map(callbackFn),
                custom: {
                  ...prevState.custom,
                  exercises: prevState.custom.exercises.map((item) => {
                    if (item.Id !== id) {
                      return item;
                    }

                    return res.data;
                  })
                }
              };
            });
          }
        });
      } else {
        const { Sub, GroupId } = visibleProfile;
        const parameters = {
          ...values,
          id: params?.Id,
          exerciseId: id,
          exerciseName: values.exerciseName.toLowerCase(),
          sub: Sub,
          groupId: GroupId
        };

        await services.exercises.addCustomParameters(parameters).then((res) => {
          if (res.status === 200) {
            setExercises((prevState) => {
              const parameter = [...prevState.custom.parameter];
              const index = parameter.findIndex((p) => p.Id === res.data.Id);

              if (index > -1) {
                parameter.splice(index, 1, res.data);
              } else {
                parameter.push(res.data);
              }

              return {
                ...prevState,
                custom: {
                  ...prevState.custom,
                  parameter
                },
                list: prevState.list.map((item) => {
                  if (res.data.ExerciseId !== item.id) {
                    return item;
                  }

                  return {
                    ...item,
                    exerciseName: res.data.ExerciseName || item.exerciseName,
                    instructions: res.data.Instructions || item.instructions,
                    sets: res.data.Sets || item.sets,
                    reps: res.data.Reps || item.reps,
                    hold: res.data.Hold || item.hold
                  };
                })
              };
            });
          }
        });
      }

      handleExerciseDetailsModal(null);
      setNotification(
        'success',
        'Success!',
        `Custom exercise ${!!custom ? '' : 'parameters'} successfully ${
          !!custom ? 'updated' : 'added'
        }.`
      );
    } catch (error) {
      setNotification(
        'error',
        'Error!',
        `An error occurred while ${
          !!custom ? 'updating' : 'adding'
        } custom exercise ${!!custom ? '' : 'parameters'}.`
      );
    } finally {
      setEditing(false);
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDropdown(!dropdown);

    try {
      const { Sub } = visibleProfile;
      await services.exercises.deleteCustomExercise(id, Sub).then((res) => {
        if (res.status === 204) {
          setExercises((prevState) => {
            const exercises = prevState.custom.exercises;

            return {
              ...prevState,
              custom: {
                ...prevState.custom,
                exercises: exercises.filter((ex) => ex.Id !== id)
              },
              unmapped: prevState.unmapped.filter((ex) => ex.id !== id),
              list: prevState.list.filter((ex) => ex.id !== id)
            };
          });

          handleExerciseDetailsModal(null);
          setNotification(
            'success',
            'Success!',
            'This exercise has been deleted.'
          );
        } else {
          throw new Error('Delete Failed!');
        }
      });
    } catch (error) {
      setNotification(
        'error',
        'Error!',
        'An error occurred while deleting custom exercise.'
      );
    }
  };

  const exceedsFavoriteLimit = () => {
    return favorites.list && favorites.list.length >= 98;
  };

  return (
    <Modal
      visible
      onCancel={() => {
        handleExerciseDetailsModal(null);
        setEditing(false);
      }}
      footer={null}
      className="preview-exercise-modal"
      width={700}
    >
      {embeddedUrl ? (
        <div className="preview-iframe-wrapper">
          <iframe
            title="Exercise Video"
            src={embeddedUrl}
            width="100%"
            frameBorder="0"
            allowFullScreen
          />
        </div>
      ) : (
        <NoResults content="No video embedded on this exercise." />
      )}
      <div className="preview-modal-body">
        <div className="tab-header">
          <Typography.Title level={4}>
            {editing ? 'Edit Exercise' : V.titleCase(exerciseName || '')}
          </Typography.Title>
          <Space
            direction="horizontal"
            size={4}
            style={{
              alignItems: 'flex-start'
            }}
          >
            {editing ? (
              <div style={{ justifyContent: 'center' }}>
                <Divider orientation="center" type="vertical" />
                <Space direction="horizontal" size={4}>
                  <Button
                    htmlType="submit"
                    className="ptw-btn"
                    type="primary"
                    size="large"
                    form="exerciseForm"
                    loading={loading}
                  >
                    Save Changes
                  </Button>
                  <Button
                    className="ptw-btn"
                    type="default"
                    onClick={() => setEditing(!editing)}
                    size="large"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </Space>
              </div>
            ) : (
              <Fragment>
                {Array.isArray(favorites.list) && (
                  <Tooltip
                    title={`${star ? 'Remove from' : 'Add to'} Favorites`}
                  >
                    <Button
                      loading={favoriteLoading}
                      type={star ? 'link' : 'text'}
                      icon={star ? <StarFilled /> : <StarOutlined />}
                      onClick={() => {
                        handleFavorite(star);
                      }}
                      size="large"
                      shape="circle"
                    />
                  </Tooltip>
                )}

                {!!custom ? (
                  <>
                    <Tooltip title="Edit Parameters">
                      <Button
                        type="text"
                        shape="circle"
                        size="large"
                        onClick={() => {
                          setEditing(!editing);
                        }}
                        icon={<EditOutlined />}
                      />
                    </Tooltip>

                    <Popconfirm
                      placement="left"
                      title="Are you sure you want to delete this custom exercise?"
                      okText="Delete"
                      okButtonProps={{
                        type: 'danger'
                      }}
                      onConfirm={handleDelete}
                    >
                      <Tooltip title="Delete Exercise">
                        <Button
                          type="text"
                          shape="circle"
                          size="large"
                          icon={<DeleteOutlined />}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </>
                ) : (
                  <Tooltip title="Edit Parameters">
                    <Button
                      type="text"
                      shape="circle"
                      size="large"
                      onClick={() => {
                        setEditing(!editing);
                      }}
                      icon={<EditOutlined />}
                    />
                  </Tooltip>
                )}
              </Fragment>
            )}
          </Space>
        </div>
        <Form
          layout="vertical"
          id="exerciseForm"
          initialValues={{
            exerciseName: V.titleCase(exerciseName || ''),
            instructions: instructions || '',
            hold: hold || '',
            reps: reps || '',
            sets: sets || '',
            cptCode: params?.CPTCode || custom?.CPTCode || ''
          }}
          onFinish={handleSaveExercise}
        >
          <Space size={24} direction="vertical" className="preview-modal-space">
            <Row gutter={[8, 8]}>
              {editing && (
                <Fragment>
                  <Col lg={16} md={16} sm={16} xs={16}>
                    <Form.Item
                      label="Exercise Name"
                      name="exerciseName"
                      tooltip={popName}
                      rules={[
                        {
                          required: true,
                          message: 'Please input your exercise name.'
                        },
                        { max: 100, message: 'Max length is 100 only.' }
                      ]}
                    >
                      <Input
                        type="text"
                        size="large"
                        name="exerciseName"
                        value={exerciseName}
                      />
                    </Form.Item>
                    <Form.Item
                      required
                      style={{ marginBottom: -40, fontWeight: 'bold' }}
                      label="Parameters"
                      tooltip={popParameters}
                    />
                  </Col>
                  <Col lg={8} md={8} sm={8} xs={8}>
                    <Form.Item
                      label="CPT Code"
                      name="cptCode"
                      tooltip="Assign a default CPT Code to this exercise. When using the Export HEP to Visit in Prompt feature, this CPT Code will automatically be assigned. You can still update it later if needed."
                    >
                      <Select size="large" showSearch placeholder="CPT Code">
                        {cptCodes.map((item, i) => (
                          <Fragment key={i}>
                            <Select.Option value={item.CPT_Code}>
                              <Tooltip
                                placement="right"
                                title={item.Description}
                              >
                                {item.CPT_Code}
                              </Tooltip>
                            </Select.Option>
                          </Fragment>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Fragment>
              )}
              <Col lg={8} md={8} sm={24} xs={24}>
                {editing ? (
                  <Form.Item
                    label="Sets"
                    name="sets"
                    dependencies={['reps', 'hold']}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            !value &&
                            !getFieldValue('reps') &&
                            !getFieldValue('hold')
                          ) {
                            setParametersError(true);
                            return Promise.reject();
                          } else {
                            setParametersError(false);
                            return Promise.resolve();
                          }
                        }
                      })
                    ]}
                  >
                    <Input
                      type="text"
                      size="large"
                      name="sets"
                      value={sets || ''}
                    />
                  </Form.Item>
                ) : (
                  sets && (
                    <Typography.Title level={5} className="text-center">
                      {sets} Sets
                    </Typography.Title>
                  )
                )}
              </Col>
              <Col lg={8} md={8} sm={24} xs={24}>
                {editing ? (
                  <Form.Item
                    label="Reps"
                    name="reps"
                    dependencies={['sets', 'hold']}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            !value &&
                            !getFieldValue('sets') &&
                            !getFieldValue('hold')
                          ) {
                            setParametersError(true);
                            return Promise.reject();
                          } else {
                            setParametersError(false);
                            return Promise.resolve();
                          }
                        }
                      })
                    ]}
                  >
                    <Input
                      type="text"
                      size="large"
                      name="reps"
                      value={reps || ''}
                    />
                  </Form.Item>
                ) : (
                  reps && (
                    <Typography.Title level={5} className="text-center">
                      {reps} Reps
                    </Typography.Title>
                  )
                )}
              </Col>
              <Col lg={8} md={8} sm={24} xs={24}>
                {editing ? (
                  <Form.Item
                    label="Hold"
                    name="hold"
                    dependencies={['sets', 'reps']}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            !value &&
                            !getFieldValue('sets') &&
                            !getFieldValue('reps')
                          ) {
                            setParametersError(true);
                            return Promise.reject();
                          } else {
                            setParametersError(false);
                            return Promise.resolve();
                          }
                        }
                      })
                    ]}
                  >
                    <Input
                      type="text"
                      size="large"
                      name="hold"
                      value={hold || ''}
                    />
                  </Form.Item>
                ) : (
                  hold && (
                    <Typography.Title level={5} className="text-center">
                      {hold} Hold
                    </Typography.Title>
                  )
                )}
              </Col>
            </Row>
            {parametersError && (
              <Typography.Paragraph type="danger" style={{ marginTop: -35 }}>
                Please fill in at least one parameter field.
              </Typography.Paragraph>
            )}
            <Row style={{ marginTop: editing ? -24 : 0 }}>
              <Col lg={24} md={24} sm={24} xs={24}>
                {editing ? (
                  <Form.Item
                    label="Instructions"
                    name="instructions"
                    tooltip={popDescription}
                    rules={[
                      {
                        required: true,
                        message: 'Please input your instructions.'
                      },
                      { max: 750, message: 'Max length is 750 only.' }
                    ]}
                  >
                    <Input.TextArea
                      size="large"
                      value={instructions || ''}
                      rows={4}
                    />
                  </Form.Item>
                ) : (
                  instructions && (
                    <Typography.Text>{instructions}</Typography.Text>
                  )
                )}
              </Col>
            </Row>
          </Space>
        </Form>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  visibleProfile: state.visibleProfile
});

export default connect(mapStateToProps)(ExerciseDetailsModal);
